import { Store } from "@reduxjs/toolkit";
import Keycloak from "keycloak-js";

import {
  logout as logoutAction,
  signIn,
  updateAuthReady,
  updateNotAuthenticated,
} from "../slice";
import {
  AuthConfig,
  KeycloakSetupOptions,
  IAuthProvider,
} from "../interfaces/IAuthProvider";
import { KEYCLOAK_SESSION_ACTIVE_KEY } from "../constants";

export class KeycloakProvider implements IAuthProvider<KeycloakSetupOptions> {
  private config: AuthConfig;
  private keycloakInstance: Keycloak | null = null;
  private store: Store | null = null;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  async setup(
    options: KeycloakSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.store = options.store;
    const dispatch = this.store.dispatch;

    // Reuse existing instance
    if (this.keycloakInstance) {
      if (this.keycloakInstance.authenticated) dispatch(signIn());
      dispatch(updateAuthReady(true));
      return { authenticated: this.keycloakInstance.authenticated ?? false };
    }

    const wasSessionActive =
      localStorage.getItem(KEYCLOAK_SESSION_ACTIVE_KEY) === "true";
    const { silentCheckSsoRedirectUri = "/silent-check-sso.html", onLoad } =
      options;
    const onLoadStrategy =
      onLoad ||
      options.onLoad ||
      (!wasSessionActive ? "login-required" : "check-sso");

    localStorage.setItem(KEYCLOAK_SESSION_ACTIVE_KEY, "true");

    const keycloakConfig = {
      url: this.config.serverUrl,
      realm: this.config.realm,
      clientId: this.config.clientId,
    };
    this.keycloakInstance = new Keycloak(keycloakConfig);

    try {
      if (!window.navigator.onLine) throw new Error("No internet connection");

      const authenticated = await this.keycloakInstance.init({
        onLoad: onLoadStrategy,
        ...(onLoadStrategy === "check-sso" && {
          silentCheckSsoRedirectUri:
            window.location.origin + silentCheckSsoRedirectUri,
          silentCheckSsoFallback: true,
        }),
        checkLoginIframe: false,
        enableLogging: true,
        pkceMethod: "S256",
      });

      if (authenticated) {
        dispatch(signIn());
      } else {
        dispatch(updateNotAuthenticated());
      }

      dispatch(updateAuthReady(true));

      return { authenticated };
    } catch (error) {
      console.log("Failed to initialize Keycloak", error);
      return { authenticated: false };
    }
  }

  async login(redirectUri?: string): Promise<void> {
    try {
      await this.keycloakInstance?.login({
        redirectUri: redirectUri || window.location.href,
      });
    } catch (error) {
      const errorMessage =
        typeof error === "string" ? error : (error as Error).message;
      console.log("Failed to login with Keycloak", errorMessage);
    }
  }

  async logout(ignoreDispatch = false): Promise<void> {
    try {
      localStorage.removeItem(KEYCLOAK_SESSION_ACTIVE_KEY);
      if (this.keycloakInstance) {
        // get the root url without query params
        const rootUrl = window.location.origin.split("?")[0];
        await this.keycloakInstance.logout({
          redirectUri: rootUrl,
        });
        this.keycloakInstance = null;
        if (!ignoreDispatch && this.store) {
          this.store.dispatch(logoutAction());
        }
      }
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  async getToken(): Promise<string | undefined> {
    try {
      if (this.keycloakInstance) {
        await this.keycloakInstance.updateToken(30);
        return this.keycloakInstance.token;
      }
    } catch (error) {
      console.log(error);
    }
    return undefined;
  }

  getCurrentToken(): string | undefined {
    return this.keycloakInstance?.token;
  }

  getClientId(): string {
    return this.config.clientId;
  }

  isInternalAccount(): boolean {
    const roles = this.keycloakInstance?.tokenParsed?.realm_access?.roles || [];
    const hasAccess = roles.some((role) => ["QA", "ADMIN"].includes(role));
    return hasAccess;
  }
}
